import { useState, useEffect } from "react";
import { translations } from "../../i18n";
import { trackInitiateCheckout } from "../../lib/pixel";
import { buildRegisterUrl } from "../../lib/tracking-url";
import Logo from "./Logo";
import RippleButton from "./RippleButton";
import LanguageSwitcher from "./LanguageSwitcher";

type Translation = (typeof translations)["de"];
type Lang = "de" | "en";

export type NavLinkKey = "features" | "pricing" | "how" | "blog";

const DEFAULT_LINKS: NavLinkKey[] = ["features", "pricing", "how", "blog"];

function Navbar({
  lang,
  onLangChange,
  t,
  theme = "dark",
  activePage,
  logoHref = "/",
  showLogin = false,
  showLangSwitcher = true,
  links = DEFAULT_LINKS,
  primaryCtaLabel,
  primaryCtaHref = buildRegisterUrl("https://app.neosearch.neobim.ai/start"),
  compactMobile = false,
  compactMobileCtaLabel,
  onPrimaryCtaClick,
}: {
  lang: Lang;
  onLangChange: (lang: Lang) => void;
  t: Translation["nav"];
  theme?: "dark" | "light";
  activePage?: "home" | "blogs";
  logoHref?: string;
  showLogin?: boolean;
  showLangSwitcher?: boolean;
  /**
   * Which middle-nav links to render. Pass `[]` to render none, or a subset
   * (e.g. `["pricing"]`) to render only specific entries. Defaults to all.
   */
  links?: NavLinkKey[];
  primaryCtaLabel?: string;
  primaryCtaHref?: string;
  /**
   * Mobile shows logo + a short "get started" CTA (not login), so the header
   * itself drives conversion. The long primary CTA label can't fit at 375px,
   * so the mobile button uses the short `t.getStarted` label instead.
   */
  compactMobile?: boolean;
  /** Overrides the short `t.getStarted` label used when `compactMobile`. */
  compactMobileCtaLabel?: string;
  /** Replaces the default InitiateCheckout tracking (e.g. a demo booking). */
  onPrimaryCtaClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isDark = theme === "dark";

  // Theme-based styles
  const navBg = isDark
    ? "bg-slate-950/95 border-slate-800/50"
    : scrolled
    ? "bg-white/95 border-slate-200/80 shadow-sm"
    : "bg-white/80 border-transparent";

  const linkClass = isDark
    ? "text-slate-400 hover:text-white"
    : "text-slate-600 hover:text-slate-900";

  const activeLinkClass = isDark
    ? "text-orange-400"
    : "text-orange-600";

  const linkConfig: Record<NavLinkKey, { href: string; label: string; activeWhen?: typeof activePage }> = {
    features: { href: "/#features", label: t.features },
    pricing: { href: "/#pricing", label: t.pricing },
    how: { href: "/#how", label: t.how },
    blog: { href: "/blogs", label: t.blog, activeWhen: "blogs" },
  };

  return (
    <header>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b py-4 transition-all duration-300 ${navBg}`}
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
          <a href={logoHref}>
            <Logo size="sm" theme={theme} />
          </a>
          <div className="hidden md:flex items-center gap-6">
            {links.map((key) => {
              const item = linkConfig[key];
              const isActive = item.activeWhen && activePage === item.activeWhen;
              return (
                <a
                  key={key}
                  href={item.href}
                  className={`${isActive ? activeLinkClass : linkClass} transition-all relative group`}
                >
                  {item.label}
                  <span
                    className={`absolute bottom-0 left-0 h-0.5 bg-orange-500 transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </a>
              );
            })}
            {showLangSwitcher && (
              <LanguageSwitcher lang={lang} onChange={onLangChange} t={t} theme={theme} />
            )}
            {showLogin && (
              <a
                href="https://app.neosearch.neobim.ai/login"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} transition-all`}
              >
                {t.login}
              </a>
            )}
            <a
              href={primaryCtaHref}
              onClick={
                onPrimaryCtaClick ??
                (() => trackInitiateCheckout({ source: "navbar" }))
              }
            >
              <RippleButton className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-all hover:scale-105 hover:shadow-lg hover:shadow-orange-500/50 active:scale-95">
                {primaryCtaLabel ?? t.startMonitoring}
              </RippleButton>
            </a>
          </div>
          <div className="flex items-center gap-3 md:hidden">
            {showLangSwitcher && !compactMobile && (
              <LanguageSwitcher lang={lang} onChange={onLangChange} t={t} theme={theme} />
            )}
            {/* Compact mobile drops login for a short conversion CTA; other
                pages keep login beside the full CTA. */}
            {showLogin && !compactMobile && (
              <a
                href="https://app.neosearch.neobim.ai/login"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} text-sm font-medium whitespace-nowrap`}
              >
                {t.login}
              </a>
            )}
            <a
              href={primaryCtaHref}
              onClick={
                onPrimaryCtaClick ??
                (() => trackInitiateCheckout({ source: "navbar" }))
              }
            >
              <RippleButton className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-all hover:scale-105 active:scale-95">
                {compactMobile
                  ? compactMobileCtaLabel ?? t.getStarted
                  : primaryCtaLabel ?? t.startMonitoring}
              </RippleButton>
            </a>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
